/**
 * React bindings for the USB scale connection.
 *
 * The connection itself lives outside React in `lib/scale/scale-store` — see
 * that module for why. This is the subscription layer plus the one piece of
 * behaviour every weight field wants: auto-capture.
 * @example
 * ```tsx
 * const scale = useScale();
 * if (!scale.supported) return null;
 * return scale.connected
 *   ? <span>{scale.pounds?.toFixed(2)} lb</span>
 *   : <button onClick={scale.connect}>Connect scale</button>;
 * ```
 */
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import { shouldCapture, shouldRearm } from 'lib/scale/auto-capture';
import { type ScaleReading, scaleStatusMessage } from 'lib/scale/hid-pos-scale';
import {
  connectScale,
  disconnectScale,
  getScaleState,
  restoreScale,
  type ScaleConnectionStatus,
  type ScaleDevice,
  type ScaleSource,
  selectScale,
  subscribeToScale,
} from 'lib/scale/scale-store';

export interface UseScaleResult {
  /**
   * Whether either transport could work here. False only on Safari, which has
   * neither WebHID nor a usable loopback WebSocket — hide the scale UI then.
   */
  supported: boolean;
  connected: boolean;
  connecting: boolean;
  status: ScaleConnectionStatus;
  /** Label of the selected scale, e.g. "DYMO M10". */
  deviceName: string | null;
  /** Every scale available right now — more than one on a bench station. */
  devices: ScaleDevice[];
  /** Which scale weight fields are reading from. */
  selectedId: string | null;
  /** Choose a scale, or `null` for none. Remembered for next time. */
  select: (deviceId: string | null) => void;
  /** Which transport is live: the desktop station, or the browser itself. */
  source: ScaleSource;
  /** Station name, when served by Farmshare Desktop. */
  stationName: string | null;
  /** Whether pairing is this page's job. False on a bridge station. */
  canPair: boolean;
  /** True once inactivity closed the station connection. */
  suspended: boolean;
  /**
   * A scale was live and no longer is.
   *
   * Distinct from simply having no scale: a browser that never had one is
   * working normally, while a bench that was weighing a moment ago is not, and
   * only the second is worth putting in front of the operator.
   */
  lost: boolean;
  /**
   * What's wrong with the scale right now, if anything.
   *
   * Rendered by the weight field itself rather than as a row beneath it, so a
   * healthy bench shows an ordinary input and nothing else.
   */
  problem: string | null;
  reading: ScaleReading | null;
  /** Live weight in pounds, or `null` before a reading or on an odd unit. */
  pounds: number | null;
  /** Whether the platter has settled — the gate on capturing. */
  stable: boolean;
  /** Something the operator must fix on the hardware, if anything. */
  statusMessage: string | null;
  error: string | null;
  /** Opens the device picker. Must be called from a user gesture. */
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

/**
 * Subscribe to the scale, reconnecting to a previously paired one on mount.
 * @returns Live scale state and the connect/disconnect actions.
 */
export function useScale(): UseScaleResult {
  const state = useSyncExternalStore(subscribeToScale, getScaleState);

  // Permission grants persist across reloads, so a station that paired its
  // scale once is connected again by the time the operator reaches a field.
  useEffect(() => {
    void restoreScale();
  }, []);

  const connect = useCallback(() => connectScale(), []);
  const disconnect = useCallback(() => disconnectScale(), []);
  const select = useCallback(
    (deviceId: string | null) => selectScale(deviceId),
    [],
  );

  // Everything below reads from the selected scale only. A station can have
  // several reporting at once, and a weight field must never quietly show a
  // number that came off a different platter.
  const selected = state.devices.find((d) => d.id === state.selectedId) ?? null;
  const reading = state.selectedId
    ? (state.readings[state.selectedId] ?? null)
    : null;

  const connected = state.status === 'connected' && !!selected?.connected;
  const lost = state.everConnected && !connected;

  // Ordered by what the operator can act on first: a scale that's plugged in
  // but misconfigured is a different job from one that isn't there.
  const statusMessage = scaleStatusMessage(reading);
  let problem: string | null = null;
  if (statusMessage) problem = statusMessage;
  else if (state.suspended) problem = 'Scale paused — click to resume';
  else if (state.source === 'bridge' && state.devices.length === 0) {
    problem = `No scale bound on ${state.stationName ?? 'this station'}`;
  } else if (lost) problem = 'Scale disconnected';

  return {
    supported: state.status !== 'unsupported',
    connected,
    connecting: state.status === 'connecting',
    status: state.status,
    deviceName: selected?.label ?? null,
    devices: state.devices,
    selectedId: state.selectedId,
    select,
    source: state.source,
    stationName: state.stationName,
    suspended: state.suspended,
    lost,
    problem,
    // On a station the desktop app owns the device; the operator pairs it
    // there, and this page has no button worth showing.
    canPair:
      state.source !== 'bridge' &&
      typeof navigator !== 'undefined' &&
      !!navigator.hid,
    reading,
    pounds: reading?.pounds ?? null,
    stable: reading?.stable ?? false,
    statusMessage,
    error: state.error,
    connect,
    disconnect,
  };
}

/**
 * Default dwell — long enough for a bench scale to finish climbing.
 *
 * Tuned against the scale on the bench. Long enough that a still-rising reading
 * doesn't get committed, short enough that the operator never waits on it.
 */
export const DEFAULT_DWELL_MS = 300;

/**
 * Weight change, in pounds, small enough to be noise rather than movement.
 *
 * Not a user setting: it describes scale hardware, not a preference, and a
 * value large enough to matter would let a slow climb through.
 */
const JITTER = 0.02;

export interface UseScaleAutoCaptureOptions {
  /** Turns the behaviour on. Off by default at every call site. */
  enabled: boolean;
  /** Receives the settled weight, in pounds. */
  onCapture: (pounds: number) => void;
  /** Ignore settled weights at or below this, in pounds. */
  minimumPounds?: number;
  /**
   * How long the weight must hold steady before it counts, in milliseconds.
   *
   * Scales assert "stable" optimistically — many flag it while the reading is
   * still climbing — so acting on the flag alone captures a weight lower than
   * what the package actually weighs.
   */
  dwellMs?: number;
}

/**
 * Fill a field automatically once the scale settles.
 *
 * The whole point of a bench scale is that the operator's hands are full: set
 * the package down, and the weight lands in the field without anyone reaching
 * for a keyboard.
 *
 * The subtlety is re-arming. A scale reports "stable" continuously while a
 * package sits on it, so a naive implementation re-fires forever. This captures
 * once per package and won't capture again until the platter has been cleared,
 * which matches how the operator is already working: on, capture, off, next.
 *
 * "Cleared" has to be judged three ways, because scales don't agree on how they
 * say it. HID scales send an explicit `stableAtZero`; serial scales generally
 * don't, and one that only transmits settled readings never reports motion
 * either — so an empty platter looks identical to a full one apart from the
 * number. Falling back to "stable below the threshold" is what keeps those
 * scales from capturing exactly once and then going dead.
 * @param options - See {@link UseScaleAutoCaptureOptions}.
 * @param options.enabled
 * @param options.onCapture
 * @param options.minimumPounds
 * @param options.dwellMs
 */
export function useScaleAutoCapture({
  enabled,
  onCapture,
  minimumPounds = 0.01,
  dwellMs = DEFAULT_DWELL_MS,
}: UseScaleAutoCaptureOptions): void {
  const { reading } = useScale();
  const armed = useRef(true);
  /** The weight the current dwell is timing, so jitter doesn't restart it. */
  const anchor = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Kept in a ref so a caller passing an inline closure doesn't re-run the
  // effect on every render and re-capture the weight already on the platter.
  const onCaptureRef = useRef(onCapture);
  onCaptureRef.current = onCapture;

  const cancel = useCallback(() => {
    if (timer.current === null) return;
    clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => {
    if (!enabled) {
      cancel();
      anchor.current = null;
      return;
    }
    if (!reading) return;

    // Cleared — the next settled weight is a new package.
    if (shouldRearm(reading, minimumPounds)) {
      cancel();
      armed.current = true;
      anchor.current = null;
      return;
    }

    if (!shouldCapture(reading, minimumPounds, armed.current)) return;

    const pounds = reading.pounds as number;

    // A reading within a hair of the weight already being timed must leave the
    // running dwell alone. Restarting it — even anchored to the same value —
    // means a scale that oscillates within its own resolution never finishes a
    // dwell and never captures at all.
    const holding =
      timer.current !== null &&
      anchor.current !== null &&
      Math.abs(pounds - anchor.current) <= JITTER;
    if (holding) return;

    // The dwell is a timer rather than a count of readings because HID scales
    // report on change only — a genuinely settled scale goes silent, so there
    // is no further reading to count on. A weight that is still climbing lands
    // here with each new value and pushes the capture out.
    cancel();
    anchor.current = pounds;
    timer.current = setTimeout(() => {
      timer.current = null;
      armed.current = false;
      anchor.current = null;
      onCaptureRef.current(pounds);
    }, dwellMs);
  }, [cancel, dwellMs, enabled, minimumPounds, reading]);

  // Drop a pending dwell if the field unmounts mid-weigh, so a closed dialog
  // can't commit a package behind the operator.
  useEffect(() => cancel, [cancel]);
}
